import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCompositeData } from '../../../hooks/storeHooks/useSessionState'
import type { TranscriptMessage } from '../../../types/transcript'
import { endMessage, systemMessage } from './constants'

interface UseCompositeProps {
  avatarReady: boolean
  addTranscript: (transcript: TranscriptMessage) => void
}

const splitRegex = /[^!?;.]+[!?;.]?/g

export const useComposite = ({
  avatarReady,
  addTranscript,
}: UseCompositeProps) => {
  const {
    compositeData: compositeDataState,
    voicesList,
    dataChannel,
  } = useCompositeData()

  const [curSentencesArr, setCurSentencesArr] = useState<string[]>([])
  const [isCompositeEnded, setIsCompositeEnded] = useState(false)
  const [sentenceIdx, setSentenceIdx] = useState<number | null>(null)
  const [talkingState, setCompositeTalkingState] = useState<string>('')
  // prevent recalculate text on each data refetch. because changes only assets
  const [compositeData, setCompositeData] = useState(compositeDataState)

  const [compositeIdx, setCompositeIdx] = useState(0)
  const [progressSentenceIdx, setProgressSentenceIdx] = useState<number>(0)
  const [currentVoice, setCurrentVoice] = useState(() =>
    voicesList?.find(voice => voice.provider === 'OpenAI'),
  )

  const totalSentenceArr = useMemo(
    () =>
      compositeData?.reduce((acc, cur) => {
        const sentences = cur.script.match(splitRegex) || []
        return acc + sentences.length
      }, 0) || 0,
    [compositeData],
  )

  const totalWordCount = useMemo(() => {
    return (
      compositeData?.reduce((acc, cur) => {
        const words = cur.script.trim().split(/\s+/)
        return acc + words.length
      }, 0) || 0
    )
  }, [compositeData])

  const estimatedSpeakingTime = useMemo(() => {
    const avgWordsPerSecond = 2.5
    const time = totalWordCount / avgWordsPerSecond
    const minutes = Math.floor(time / 60)

    return `~ ${minutes + 1} min`
  }, [totalWordCount])

  const compositeProgress = useMemo(
    () =>
      totalSentenceArr > 0 ? (progressSentenceIdx / totalSentenceArr) * 100 : 0,
    [totalSentenceArr, progressSentenceIdx],
  )

  const isTts = useMemo(
    () => currentVoice?.provider === 'OpenAiTts',
    [currentVoice],
  )

  const sendSpeech = useCallback(
    (text: string) => {
      const data = {
        type: 'talk',
        data: {
          talkId: crypto.randomUUID(),
          text,
          split: true,
        },
      }
      dataChannel?.send(JSON.stringify(data))
    },
    [dataChannel],
  )

  const setVoice = useCallback(
    (voiceId: string) => {
      const data = {
        type: 'activate_voice',
        data: { voice_id: voiceId },
      }
      dataChannel?.send(JSON.stringify(data))
    },
    [dataChannel],
  )

  const sendSystemMessage = useCallback(
    (text: string, trigger_response: boolean) => {
      const data = {
        type: 'send_message',
        data: {
          role: 'system',
          text,
          trigger_response,
          delay: false,
        },
      }
      dataChannel?.send(JSON.stringify(data))
    },
    [dataChannel],
  )

  const handleTalk = useCallback(
    (sentences: string[]) => {
      const speech = sentences.join(' ')
      console.log('Sending speech:', speech)
      sendSpeech(speech)
      addTranscript({ role: 'mentor', content: speech })
    },
    [compositeData, sentenceIdx, curSentencesArr, sendSpeech, addTranscript],
  )

  const handleSetComposite = useCallback(() => {
    setCompositeIdx(prevComposite => {
      if (prevComposite + 1 === compositeData?.length) {
        setIsCompositeEnded(true)
        setOpenAiVoice()
        setTimeout(() => {
          sendSystemMessage(endMessage, true)
        }, 100)
        return prevComposite
      }
      const next = prevComposite + 1
      setSentenceIdx(null)
      const nextSentences =
        compositeData?.[next]?.script.match(splitRegex) || []
      setCurSentencesArr(nextSentences)
      handleTalk(nextSentences)
      return next
    })
  }, [handleTalk, compositeData])

  const handleChangeVoiceMode = useCallback(
    (type: string) => {
      const newVoice = voicesList?.find(voice => voice.provider === type)
      if (newVoice) {
        setVoice(newVoice.id)
        setCurrentVoice(newVoice)
      }
      if (type === 'OpenAiTts') {
        setTimeout(() => {
          const sentences = curSentencesArr.length
            ? curSentencesArr
            : compositeData?.[compositeIdx]?.script.match(splitRegex) || []
          handleTalk(sentences)
        }, 500)
      } else {
        setCurSentencesArr(prevSentencesArr => {
          if (!isCompositeEnded) {
            setProgressSentenceIdx(cur => cur + 1)
          }
          setSentenceIdx(null)
          return prevSentencesArr.slice(sentenceIdx !== null ? sentenceIdx : 0)
        })
      }
    },
    [voicesList, setVoice, handleTalk, curSentencesArr, sentenceIdx],
  )

  const setTtsVoice = useCallback(
    () => handleChangeVoiceMode('OpenAiTts'),
    [handleChangeVoiceMode],
  )

  const setOpenAiVoice = useCallback(() => {
    handleChangeVoiceMode('OpenAI')
  }, [handleChangeVoiceMode])

  const handleSetSentence = useCallback(
    (idx: number | null) => {
      setSentenceIdx(sentenceIdx => {
        if (idx !== null && idx !== sentenceIdx) {
          setProgressSentenceIdx(cur => cur + 1)
        }
        return idx
      })
    },
    [sentenceIdx],
  )

  const handleRepeatCurrentSpeech = useCallback(() => {
    if (!isTts) {
      setCompositeTalkingState('preparing')
      setTtsVoice()
    } else {
      setCurSentencesArr(prevSentencesArr => {
        const sentences = prevSentencesArr.slice(
          sentenceIdx !== null ? sentenceIdx : 0,
        )
        if (sentenceIdx) {
          setProgressSentenceIdx(cur => cur - 1)
          setSentenceIdx(null)
        }
        handleTalk(sentences)
        return sentences
      })
    }
  }, [compositeIdx, curSentencesArr, sentenceIdx])

  useEffect(() => {
    if (dataChannel?.readyState === 'open' && compositeData && avatarReady) {
      const message = `${systemMessage} \n{VOICE-OVER SCRIPT} = ${JSON.stringify(
        compositeData,
      )}`
      sendSystemMessage(message, false)

      setCurSentencesArr(
        compositeData[compositeIdx]?.script.match(splitRegex) || [],
      )
      setTtsVoice()
    }
  }, [dataChannel?.readyState, compositeData, avatarReady, sendSystemMessage])

  useEffect(() => {
    if (isTts && talkingState === 'ended') {
      handleSetComposite()
    }
  }, [talkingState, isTts])

  useEffect(() => {
    if (!compositeData?.length) {
      setCompositeData(compositeDataState)
    }
  }, [compositeDataState, compositeData])

  return {
    setOpenAiVoice,
    handleSetSentence,
    handleRepeatCurrentSpeech,
    setCompositeTalkingState,
    isCompositeEnded,
    compositeProgress,
    estimatedSpeakingTime,
    visualData: compositeDataState?.[compositeIdx]?.visualElements,
  }
}
